<template>
    <InfoBaseForm v-model="formData" :is-loading="isLoading" @save="save()" @cancel="emit('cancel')">
        <template v-slot:center-block>
            <v-row v-if="serviceType !== contractServiceType.ownExtraService" dense>
                <v-col cols="12" sm="5" md="4">
                    <v-autocomplete
                        v-model="formData.serviceTypeId"
                        :items="serviceTypes"
                        item-value="id"
                        item-title="typeName"
                        :label="`${$t('type')}*`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="service-type-id"
                        :disabled="
                            isLoadingServiceTypes || (serviceType === contractServiceType.concert && !!editServiceId)
                        "
                        :rules="[requiredRule('type')]"
                    />
                </v-col>
                <v-col cols="12" sm="5" md="4">
                    <v-autocomplete
                        v-model="formData.categoryId"
                        :items="useCatalogue"
                        item-value="id"
                        item-title="name"
                        :label="`${$t('category')}*`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="category-id"
                        :disabled="!!editServiceId"
                        :rules="[requiredRule('category')]"
                    />
                </v-col>
                <v-col v-if="!editServiceId && serviceType !== contractServiceType.ownHotel" cols="12" sm="2" md="4">
                    <v-btn
                        size="small"
                        icon="mdi-plus"
                        variant="text"
                        color="primary"
                        data-cy="add-service"
                        @click="isShowModal = true"
                    />
                </v-col>
            </v-row>
            <ModalWrapper v-model="isShowModal">
                <template v-slot:title>
                    {{ $t('product.info.create_category') }}
                </template>
                <template v-slot:content>
                    <InfoCatalogueForm
                        ref="infoCatalogue"
                        hide-buttons
                        :contract-id="contractId"
                        @save="isShowModal = false"
                    />
                </template>
                <template v-slot:btns>
                    <v-btn
                        :disabled="!infoCatalogue?.valid"
                        data-cy="save"
                        variant="flat"
                        @click="infoCatalogue.save()"
                    >
                        {{ $t('save') }}
                    </v-btn>
                </template>
            </ModalWrapper>
        </template>
        <template v-slot:bottom-block>
            <div>
                <template v-if="serviceType === contractServiceType.concert">
                    <div class="text-subtitle-1">{{ $t('number_of_tourists') }}:</div>
                    <v-row dense>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.minOccupancy"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="min-occupancy"
                                width="100px"
                            />
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.maxOccupancy"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="max-occupancy"
                                width="100px"
                            />
                        </v-col>
                    </v-row>
                </template>
                <template
                    v-if="
                        serviceType === contractServiceType.ownCruise ||
                        serviceType === contractServiceType.tour ||
                        serviceType === contractServiceType.customComplexTour
                    "
                >
                    <v-row dense>
                        <v-col cols="3" md="2">
                            <div class="text-subtitle-1 width-max-content">
                                {{ $t('product.info.capacity') }}*
                                <v-tooltip location="top" activator="parent" class="text-body-2">
                                    {{ $t('product.info.capacity_note') }}
                                </v-tooltip>
                            </div>
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.minOccupancy"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                :label="$t('min')"
                                color="primary"
                                density="compact"
                                data-cy="min-capacity"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.capacity')]"
                                @change="calculateMinTotalOccupancy"
                            />
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.maxOccupancy"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                :label="$t('max')"
                                data-cy="max-capacity"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.capacity')]"
                                @change="calculateMaxTotalOccupancy"
                            />
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="3" md="2">
                            <div class="text-subtitle-1 width-max-content">
                                {{ $t('product.info.extra_bed') }}*
                                <v-tooltip location="top" activator="parent" class="text-body-2">
                                    {{ $t('product.info.extra_bed_note') }}
                                </v-tooltip>
                            </div>
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.minExtraplace"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="min-extra-bed"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.extra_bed')]"
                                @change="calculateMinTotalOccupancy"
                            />
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.maxExtraplace"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="max-extra-bed"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.extra_bed')]"
                                @change="calculateMaxTotalOccupancy"
                            />
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="3" md="2">
                            <div class="text-subtitle-1 width-max-content">
                                {{ $t('product.info.sharing_place') }}*
                                <v-tooltip location="top" activator="parent" class="text-body-2">
                                    {{ $t('product.info.sharing_place_note') }}
                                </v-tooltip>
                            </div>
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.minWithoutPlace"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="min-sharing-place"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.sharing_place')]"
                                @change="calculateMinTotalOccupancy"
                            />
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.maxWithoutPlace"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="max-sharing-place"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.sharing_place')]"
                                @change="calculateMaxTotalOccupancy"
                            />
                        </v-col>
                    </v-row>
                    <v-row dense class="mb-10">
                        <v-col cols="3" md="2">
                            <div class="text-subtitle-1 width-max-content">
                                {{ $t('product.info.total') }}*
                                <v-tooltip location="top" activator="parent" class="text-body-2">
                                    {{ $t('product.info.total_note') }}
                                </v-tooltip>
                            </div>
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.minTotalOccupancy"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                disabled
                                color="primary"
                                density="compact"
                                data-cy="min-total"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.total')]"
                            />
                        </v-col>
                        <v-col class="flex-grow-0">
                            <v-text-field
                                v-model="formData.maxTotalOccupancy"
                                type="number"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                disabled
                                density="compact"
                                data-cy="max-total"
                                hide-details
                                width="70px"
                                :rules="[requiredRule('product.info.total')]"
                            />
                        </v-col>
                    </v-row>
                </template>
            </div>
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import InfoBaseForm from '~src/components/products/information/InfoBaseForm.vue'
    import {initCruiseAccommodationFormData, initEventServiceFormData} from '~/utils/types/account/products/helpers'
    import InfoCatalogueForm from '~/components/products/information/InfoCatalogueForm.vue'
    import {requiredRule} from '~/composables/rules'
    import {contractServiceType} from '~/utils/types/types'
    import {useCatalogue} from '~/composables/form-helpers'

    const props = defineProps<{
        editServiceId?: number | null
        contractId: number
        serviceType: string
    }>()

    const isLoading = ref<boolean>(false)
    const formData = ref<any>(initBaseFormData())
    const isShowModal = ref<boolean>(false)
    const isLoadingServiceTypes = ref<boolean>(false)
    const serviceTypes = ref([])
    const infoCatalogue = ref(null)

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.editServiceId) await loadData()
        await loadServiceType()
    })

    function initBaseFormData() {
        switch (props.serviceType) {
            case contractServiceType.concert:
                return initEventServiceFormData()
            case contractServiceType.ownCruise:
                return initCruiseAccommodationFormData()
            case contractServiceType.tour:
            case contractServiceType.customComplexTour:
                return initCruiseAccommodationFormData()
            default:
                return {name: {}, description: {}, active: true}
        }
    }

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {id: props.editServiceId, locale: 'all'}
            const {services} = await app.$api.ownProductServices.get(rq)
            formData.value = services[0]
        } catch (e) {
            formData.value = initEventServiceFormData()
        } finally {
            isLoading.value = false
        }
    }

    async function loadServiceType(): Promise<void> {
        try {
            isLoadingServiceTypes.value = true
            const {object} = await app.$api.ownProductServiceType.get(props.serviceType)
            serviceTypes.value = object.sort((a, b) => a.typeName.localeCompare(b.typeName))
        } catch (e) {
            serviceTypes.value = []
        } finally {
            isLoadingServiceTypes.value = false
        }
    }

    async function calculateMinTotalOccupancy(): Promise<void> {
        formData.value.minTotalOccupancy =
            +formData.value.minOccupancy + +formData.value.minExtraplace + +formData.value.minWithoutPlace
    }

    async function calculateMaxTotalOccupancy(): Promise<void> {
        formData.value.maxTotalOccupancy =
            +formData.value.maxOccupancy + +formData.value.maxExtraplace + +formData.value.maxWithoutPlace
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(formData.value)
            clean(data)
            if (props.editServiceId) {
                await app.$api.ownProductServices.put({
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownProductServices.post({
                    contractId: props.contractId,
                    ...data,
                })
            }

            await supplierStore.loadProductServices()
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
